import { useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useState } from 'react';
import { useDebounceCallback, useDebounceValue } from 'usehooks-ts';
import { useUpdateEffect } from '../../hooks/useCustomEffects';
import { getForms } from '../../services/formsService';
import { IdName } from '../../types/app';
import { FormCore } from '../../types/forms';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';

type Props = {
  form?: IdName;
  forms?: IdName[];
  selectedFormId?: string;
  selectedFormIds?: string[];
  onChangeFormId?: (formId?: string) => void;
  onChangeForm?: (form?: FormCore) => void;
  onChangeFormIds?: (formIds?: string[]) => void;
  onChangeForms?: (forms?: FormCore[]) => void;
  isMulti?: boolean;
  isFilter?: boolean;
  label?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  error?: React.ReactNode;
  onBlur?: () => void;
  inline?: boolean;
  clearable?: boolean;
  required?: boolean;
  maxDropdownHeight?: string;
};
export function FormSelect({
  selectedFormId,
  onChangeFormId,
  onChangeForm,
  selectedFormIds,
  onChangeFormIds,
  onChangeForms,
  form,
  forms,
  isFilter = true,
  label = 'Formulario:',
  emptyOptionText = 'Seleciona un formulario',
  showEmptyOption = false,
  error,
  onBlur,
  inline,
  clearable = false,
  required = false,
  isMulti = false,
  maxDropdownHeight,
}: Readonly<Props>) {
  const [formsList, setFormsList] = useState<FormCore[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue<string | undefined>(undefined, 350);
  const [selectedFormOption, setSelectedFormOption] = useState<{ id: string; label: string }>();
  const [selectedFormOptions, setSelectedFormOptions] = useState<{ id: string; label: string }[]>();

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    const loadForms = async () => {
      startLoading();
      const { paginatedResponse, error } = await getForms(page, debouncedSearch, 'active');
      if (!error && paginatedResponse) {
        if (page > 1 && formsList) {
          setFormsList([...formsList, ...paginatedResponse.items]);
          setHasNextPage(paginatedResponse.hasNext);
        } else {
          setFormsList(paginatedResponse.items);
          setHasNextPage(paginatedResponse.hasNext);
        }
      } else {
        setFormsList(undefined);
        setHasNextPage(false);
      }
      stopLoading();
    };
    loadForms();
  }, [page]);

  const formOptions = useMemo(() => {
    if (!formsList) return;
    const coreFormsOptions = formsList.map((form) => ({ id: form._id, label: form.name }));
    const formsOptions = showEmptyOption
      ? [
          {
            id: undefined,
            label: isFilter ? 'Todos' : emptyOptionText,
          },
          ...coreFormsOptions,
        ]
      : coreFormsOptions;

    if (form) {
      if (!formsOptions.find((c) => c.id === form._id))
        formsOptions.push({
          id: form._id,
          label: form.name,
        });
    } else if (forms) {
      forms.forEach((form) => {
        if (!formsOptions.find((d) => d.id === form._id))
          formsOptions.push({
            id: form._id,
            label: form.name,
          });
      });
    }
    if (selectedFormOption) {
      if (!formsOptions.find((c) => c.id === selectedFormOption.id)) formsOptions.push(selectedFormOption);
    } else if (selectedFormOptions) {
      selectedFormOptions.forEach((form) => {
        if (!formsOptions.find((d) => d.id === form.id) && form.id) formsOptions.push(form);
      });
    }
    return formsOptions;
  }, [formsList]);

  const onFormChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeFormId && onChangeFormId(params.option.id as string);
      onChangeForm &&
        onChangeForm({
          _id: params.option?.id,
          name: params.option?.label,
        } as FormCore);
    } else {
      onChangeFormId && onChangeFormId(undefined);
      onChangeForm && onChangeForm(undefined);
    }
    setSelectedFormOption(params.option as { id: string; label: string });
    setDebouncedSearch(undefined);
  };

  const onFormsChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeFormIds &&
        onChangeFormIds(params.value.map((val) => val.id).filter((id) => id !== undefined) as string[]);
      onChangeForms &&
        onChangeForms(
          params.value
            .map((val) => ({ _id: val.id, name: val.label }))
            .filter((option) => option._id !== undefined) as FormCore[]
        );
    } else {
      onChangeFormIds && onChangeFormIds(undefined);
      onChangeForms && onChangeForms(undefined);
    }
    setSelectedFormOptions(
      params.value.map((val) => val).filter((val) => val.id !== undefined) as { id: string; label: string }[]
    );
    setDebouncedSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage((page ?? 0) + 1);
  };

  const handleInputChange = useDebounceCallback(function (term: string) {
    setDebouncedSearch(term);
  }, 350);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={formOptions}
      value={
        isMulti
          ? showEmptyOption || selectedFormIds !== undefined
            ? selectedFormIds?.map((formId) => ({ id: formId }))
            : undefined
          : showEmptyOption || selectedFormId !== undefined
          ? [{ id: selectedFormId }]
          : undefined
      }
      onChange={isMulti ? onFormsChange : onFormChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      fullWidth
      infiniteScroll
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      onBlur={() => {
        setDebouncedSearch(undefined);
        onBlur && onBlur();
      }}
      searchable
      inline={inline}
      error={error}
      required={required}
      clearable={clearable}
      placeholder={emptyOptionText}
      // onAdd={onAddClick}
      isMulti={isMulti}
      maxDropdownHeight={maxDropdownHeight}
    />
  );
}
